import { useState, useEffect } from 'react';
import { Bell, BellOff, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function NotificationPrompt({ currentMember }) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    if (!('Notification' in window) || !currentMember) {
      return;
    }

    setPermission(Notification.permission);

    // Show prompt if not decided yet
    if (Notification.permission === 'default') {
      // Wait a bit before showing prompt (better UX)
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    // If granted, subscribe to push
    if (Notification.permission === 'granted') {
      subscribeToPush();
    }
  }, [currentMember]);

  const subscribeToPush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Get VAPID public key
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      const publicKey = data.publicKey;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to backend
      await axios.post(`${API}/push/subscribe`, {
        member_name: currentMember,
        subscription: subscription.toJSON()
      });

      console.log('Push notification subscribed');
    } catch (error) {
      console.error('Failed to subscribe to push:', error);
    }
  };

  const handleEnableNotifications = async () => {
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      setShowPrompt(false);

      if (result === 'granted') {
        toast.success('Notifications enabled! You\'ll get notified about new events');
        await subscribeToPush();
      } else {
        toast.error('Notifications disabled');
      }
    } catch (error) {
      console.error('Notification permission error:', error);
      toast.error('Failed to enable notifications');
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  if (!('Notification' in window) || permission !== 'default' || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed top-20 left-1/2 transform -translate-x-1/2 max-w-sm w-full mx-4 z-50 animate-in slide-in-from-top">
      <div className="bg-zinc-900 border-2 border-[#E11D48] rounded-lg shadow-2xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Bell className="w-6 h-6 text-[#E11D48]" />
            <h3 className="font-['Anton'] text-xl text-white uppercase tracking-tight">
              Stay Updated
            </h3>
          </div>
          <button
            onClick={() => setShowPrompt(false)}
            className="text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <p className="text-zinc-300 text-sm mb-6">
          Get notified when new events are created. Never miss out!
        </p>

        <div className="flex gap-3">
          <button
            onClick={handleEnableNotifications}
            className="flex-1 bg-[#E11D48] hover:bg-[#BE123C] text-white font-bold py-3 uppercase tracking-wider text-sm transition-colors"
          >
            Enable
          </button>
          <button
            onClick={() => setShowPrompt(false)}
            className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold py-3 uppercase tracking-wider text-sm transition-colors"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
}

export default NotificationPrompt;
